import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Download, 
  Printer, 
  FileText, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle,
  HelpCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const PRINT_AGENT_VERSION = '1.0.0';
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function Downloads() {
  const [testingPrint, setTestingPrint] = useState(false);

  const handleTestPrint = async () => {
    setTestingPrint(true);
    try {
      const response = await fetch(`${API_BASE}/api/cloud-print/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || '✅ Testdruck erfolgreich gesendet!');
      } else {
        toast.error(data.message || '❌ Testdruck fehlgeschlagen');
      }
    } catch (error) {
      toast.error('❌ Print Agent nicht erreichbar. Bitte prüfen Sie, ob der Agent läuft.');
    } finally {
      setTestingPrint(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold text-white">
            CashX Setup & Downloads
          </h1>
          <p className="text-slate-400 text-lg">
            Alles, was Sie für den Druck benötigen – in 5 Minuten einsatzbereit
          </p>
        </div>

        {/* CashX Cloud Print Agent */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-teal-500/10 rounded-lg">
                  <Printer className="h-6 w-6 text-teal-400" />
                </div>
                <div>
                  <CardTitle className="text-white">CashX Cloud Print Agent</CardTitle>
                  <CardDescription>
                    Verbindet Ihre lokalen Drucker mit CashX
                  </CardDescription>
                </div>
              </div>
              <Badge variant="outline" className="text-teal-400 border-teal-400">
                v{PRINT_AGENT_VERSION}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-blue-500/10 border-blue-500/50">
              <AlertCircle className="h-4 w-4 text-blue-400" />
              <AlertDescription className="text-blue-200">
                <strong>Wichtig:</strong> Der Print Agent muss auf einem Computer laufen, der Zugriff auf Ihre Drucker hat (z.B. Ihr Kassen-PC).
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <h4 className="font-semibold text-white">Installation in 4 Schritten:</h4>
              <ol className="space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <Badge variant="outline" className="mt-0.5">1</Badge>
                  <span>Agent herunterladen und installieren</span>
                </li>
                <li className="flex items-start gap-3">
                  <Badge variant="outline" className="mt-0.5">2</Badge>
                  <span>Agent starten (erscheint im System Tray)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Badge variant="outline" className="mt-0.5">3</Badge>
                  <span>Mit Ihrem CashX-Account anmelden</span>
                </li>
                <li className="flex items-start gap-3">
                  <Badge variant="outline" className="mt-0.5">4</Badge>
                  <span>Drucker auswählen und Testdruck durchführen</span>
                </li>
              </ol>
            </div>

            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                size="lg"
                asChild
              >
                <a href="/downloads/CashX-PrintAgent-v1.0.0.exe" download>
                  <Download className="mr-2 h-5 w-5" />
                  Download für Windows
                </a>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={handleTestPrint}
                disabled={testingPrint}
              >
                {testingPrint ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-5 w-5" />
                )}
                Testdruck
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SumatraPDF */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <FileText className="h-6 w-6 text-orange-400" />
              </div>
              <div>
                <CardTitle className="text-white">SumatraPDF</CardTitle>
                <CardDescription>
                  PDF-Viewer für automatischen Druck (empfohlen)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-slate-300">
              SumatraPDF ermöglicht den automatischen, stillen Druck von Belegen ohne manuelle Bestätigung. 
              Dies ist besonders wichtig für einen reibungslosen Kassenablauf.
            </p>
            <Button 
              variant="outline" 
              className="w-full"
              asChild
            >
              <a 
                href="https://www.sumatrapdfreader.org/download-free-pdf-viewer" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Zur offiziellen SumatraPDF Website
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Druckertreiber */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <Printer className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white">Druckertreiber</CardTitle>
                <CardDescription>
                  Treiber für Thermodrucker (58mm / 80mm)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-300">
              Für Thermodrucker benötigen Sie die offiziellen Treiber des Herstellers. 
              Das Druckermodell finden Sie meist auf der Rückseite oder Unterseite des Geräts.
            </p>

            <div className="grid md:grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                asChild
              >
                <a 
                  href="https://www.epson.de/support" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <div className="text-left">
                    <div className="font-semibold">Epson</div>
                    <div className="text-xs text-slate-400">TM-T20, TM-T88, TM-m30</div>
                  </div>
                  <ExternalLink className="ml-auto h-4 w-4" />
                </a>
              </Button>

              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                asChild
              >
                <a 
                  href="https://www.star-emea.com/download/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <div className="text-left">
                    <div className="font-semibold">Star Micronics</div>
                    <div className="text-xs text-slate-400">TSP100, TSP650, mC-Print</div>
                  </div>
                  <ExternalLink className="ml-auto h-4 w-4" />
                </a>
              </Button>

              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                asChild
              >
                <a 
                  href="https://www.bixolon.com/download/down_driver.php" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <div className="text-left">
                    <div className="font-semibold">Bixolon</div>
                    <div className="text-xs text-slate-400">SRP-330, SRP-350</div>
                  </div>
                  <ExternalLink className="ml-auto h-4 w-4" />
                </a>
              </Button>

              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                asChild
              >
                <a 
                  href="https://www.citizen-systems.co.jp/english/product/printer/download/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <div className="text-left">
                    <div className="font-semibold">Citizen</div>
                    <div className="text-xs text-slate-400">CT-S310, CT-S601</div>
                  </div>
                  <ExternalLink className="ml-auto h-4 w-4" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-500/10 rounded-lg">
                <HelpCircle className="h-6 w-6 text-yellow-400" />
              </div>
              <div>
                <CardTitle className="text-white">Häufige Probleme & Lösungen</CardTitle>
                <CardDescription>
                  Troubleshooting für Print Agent
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Problem 1 */}
            <div className="p-4 bg-slate-900/50 rounded-lg space-y-2">
              <h4 className="font-semibold text-white flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400" />
                Agent offline / nicht erreichbar
              </h4>
              <ul className="text-slate-300 text-sm space-y-1 ml-6 list-disc">
                <li>Prüfen Sie, ob der Print Agent läuft (System Tray Icon)</li>
                <li>Neustart des Print Agent durchführen</li>
                <li>Firewall-Einstellungen prüfen (Port 5000 freigeben)</li>
                <li>Internetverbindung prüfen</li>
              </ul>
            </div>

            {/* Problem 2 */}
            <div className="p-4 bg-slate-900/50 rounded-lg space-y-2">
              <h4 className="font-semibold text-white flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400" />
                Drucker nicht gefunden
              </h4>
              <ul className="text-slate-300 text-sm space-y-1 ml-6 list-disc">
                <li>Drucker einschalten und USB-Kabel prüfen</li>
                <li>Druckertreiber installiert? (siehe oben)</li>
                <li>Windows-Druckereinstellungen öffnen und Testseite drucken</li>
                <li>Print Agent neu starten nach Treiberinstallation</li>
              </ul>
            </div>

            {/* Problem 3 */}
            <div className="p-4 bg-slate-900/50 rounded-lg space-y-2">
              <h4 className="font-semibold text-white flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400" />
                Bon wird abgeschnitten
              </h4>
              <ul className="text-slate-300 text-sm space-y-1 ml-6 list-disc">
                <li>Falsches Druckerprofil gewählt? (58mm statt 80mm)</li>
                <li>In den Drucker-Einstellungen die Papierbreite korrekt setzen</li>
                <li>Schriftgröße in den CashX-Einstellungen anpassen</li>
              </ul>
            </div>

            {/* Problem 4 */}
            <div className="p-4 bg-slate-900/50 rounded-lg space-y-2">
              <h4 className="font-semibold text-white flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400" />
                A4-Drucker druckt falsch
              </h4>
              <ul className="text-slate-300 text-sm space-y-1 ml-6 list-disc">
                <li>Stellen Sie sicher, dass A4-Profil in den Einstellungen gewählt ist</li>
                <li>Überprüfen Sie die Druckereinstellungen (Hochformat, A4)</li>
                <li>Ränder in den Drucker-Eigenschaften prüfen</li>
              </ul>
            </div>

          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-slate-400 text-sm py-4">
          <p>
            Benötigen Sie weitere Hilfe? 
            <a href="mailto:support@cashx.de" className="text-teal-400 hover:underline ml-1">
              support@cashx.de
            </a>
          </p>
        </div>

      </div>
    </div>
  );
}
